import { useState } from 'react';

interface VideoItem {
  title: string;
  desc: string;
  embedUrl?: string;
}

const videos: VideoItem[] = [
  {
    title: 'Introduction to THL',
    desc: 'Get to know THL — who we are, what we do, and how we\'re transforming logistics across Nigeria.',
    embedUrl: 'https://drive.google.com/file/d/1IOE6x8qa-eHznbHA9q-z1wkpIAd7pcdX/preview',
  },
  {
    title: 'THL Technology & Innovation',
    desc: 'Discover the cutting-edge technology and innovative systems powering THL\'s logistics operations.',
    embedUrl: 'https://drive.google.com/file/d/1WXtcyvfyJ__ymToxlTzX-qoks5zalWrU/preview',
  },
  {
    title: 'THL-FAQs',
    desc: 'Find answers to the most frequently asked questions about THL\'s services, operations, and partnerships.',
    embedUrl: 'https://drive.google.com/file/d/1b2beQkdeVO7xyPDPUppOfB4vjVdvBI82/preview',
  },
];

export default function VideoSection() {
  const [activeVideo, setActiveVideo] = useState<VideoItem | null>(null);

  const openModal = (video: VideoItem) => {
    if (video.embedUrl) setActiveVideo(video);
  };

  const closeModal = () => setActiveVideo(null);

  return (
    <>
      <section className="py-24 px-6" style={{ background: '#0f0f18' }}>
        <div className="max-w-7xl mx-auto">
          <div className="mb-12 text-center">
            <p className="eyebrow">Get to Know THL</p>
            <h2 className="section-heading mb-2">See Us in Action</h2>
            <p className="section-lead max-w-xl mx-auto">
              Watch our videos to see how THL is transforming logistics for businesses across Nigeria.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {videos.map((v) => (
              <div
                key={v.title}
                className="group bg-thl-dark3 border border-thl-border rounded-xl overflow-hidden hover:border-thl-red/30 transition-all duration-200 cursor-pointer"
                onClick={() => openModal(v)}
              >
                {v.embedUrl ? (
                  <div className="w-full aspect-video relative">
                    {/* Thumbnail overlay — blocks iframe interaction so click registers on the card */}
                    <iframe
                      src={v.embedUrl}
                      className="w-full h-full pointer-events-none"
                      allow="autoplay"
                      allowFullScreen
                      title={v.title}
                      style={{ border: 'none' }}
                    />
                    {/* Play button overlay */}
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/10 transition-colors duration-200">
                      <div className="w-14 h-14 rounded-full bg-thl-red/80 border border-thl-red flex items-center justify-center group-hover:scale-110 transition-transform duration-200">
                        <span className="text-white text-xl ml-1">&#9654;</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div
                    className="w-full aspect-video flex flex-col items-center justify-center gap-3"
                    style={{ background: 'linear-gradient(135deg, #1a0a0e 0%, #1C1C28 100%)' }}
                  >
                    <div className="w-14 h-14 rounded-full bg-thl-red/20 border border-thl-red/40 flex items-center justify-center group-hover:bg-thl-red/30 transition-colors">
                      <span className="text-thl-red text-xl ml-1">&#9654;</span>
                    </div>
                    <p className="text-white/30 text-xs">Add video via Admin Panel</p>
                  </div>
                )}
                <div className="p-5 text-center">
                  <div className="text-white font-semibold text-sm mb-2">{v.title}</div>
                  <div className="text-white/50 text-xs leading-relaxed">{v.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {activeVideo && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8"
          style={{ background: 'rgba(0,0,0,0.85)' }}
          onClick={closeModal}
        >
          <div
            className="relative w-full max-w-4xl rounded-2xl overflow-hidden"
            style={{ background: '#0f0f18', border: '1px solid rgba(255,255,255,0.1)' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              className="absolute top-3 right-3 z-10 w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors text-white whitespace-nowrap cursor-pointer"
              onClick={closeModal}
              aria-label="Close video"
            >
              <i className="ri-close-line text-lg"></i>
            </button>

            {/* Video */}
            <div className="w-full aspect-video">
              <iframe
                src={activeVideo.embedUrl}
                className="w-full h-full"
                allow="autoplay"
                allowFullScreen
                title={activeVideo.title}
                style={{ border: 'none' }}
              />
            </div>

            {/* Title bar */}
            <div className="px-6 py-4">
              <div className="text-white font-semibold text-base mb-1">{activeVideo.title}</div>
              <div className="text-white/50 text-sm leading-relaxed">{activeVideo.desc}</div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
